import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CalendarIcon,
  UserIcon,
  TagIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import PlaceholderImage from '../components/PlaceholderImage';

const Blog: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'AI Strategy', 'Machine Learning', 'Data Analytics', 'Industry Insights', 'Case Studies'];

  const blogPosts = [
    {
      id: 1,
      title: 'The Future of AI in Enterprise: 2024 Trends and Predictions',
      excerpt: 'Explore the key AI trends that will shape enterprise technology in 2024, from generative AI to autonomous systems.',
      content: 'As we move into 2024, artificial intelligence continues to evolve at an unprecedented pace...',
      author: 'Dr. Sarah Johnson',
      date: '2024-01-15',
      category: 'AI Strategy',
      readTime: '8 min read',
      image: '/images/blog-ai-trends.jpg',
      tags: ['AI Trends', 'Enterprise', '2024'],
    },
    {
      id: 2,
      title: 'Building Scalable Machine Learning Pipelines',
      excerpt: 'Learn best practices for designing and implementing ML pipelines that can scale with your business needs.',
      content: 'Machine learning pipelines are the backbone of any successful AI implementation...',
      author: 'Michael Chen',
      date: '2024-01-10',
      category: 'Machine Learning',
      readTime: '12 min read',
      image: '/images/blog-ml-pipelines.jpg',
      tags: ['ML Pipelines', 'Scalability', 'Best Practices'],
    },
    {
      id: 3,
      title: 'Data-Driven Decision Making: A Complete Guide',
      excerpt: 'Discover how to transform your organization with data-driven insights and analytics.',
      content: 'In today\'s competitive business landscape, data-driven decision making is no longer optional...',
      author: 'Dr. Emily Rodriguez',
      date: '2024-01-05',
      category: 'Data Analytics',
      readTime: '10 min read',
      image: '/images/blog-data-analytics.jpg',
      tags: ['Data Analytics', 'Decision Making', 'Business Intelligence'],
    },
    {
      id: 4,
      title: 'AI in Healthcare: Transforming Patient Care',
      excerpt: 'How AI is revolutionizing healthcare delivery and improving patient outcomes.',
      content: 'The healthcare industry is experiencing a digital transformation powered by AI...',
      author: 'David Kim',
      date: '2023-12-28',
      category: 'Industry Insights',
      readTime: '15 min read',
      image: '/images/blog-healthcare-ai.jpg',
      tags: ['Healthcare', 'AI Applications', 'Patient Care'],
    },
    {
      id: 5,
      title: 'Case Study: AI-Powered Customer Service at Scale',
      excerpt: 'How we helped a Fortune 500 company implement AI chatbots and improve customer satisfaction.',
      content: 'Customer service is one of the most critical touchpoints between businesses and their customers...',
      author: 'Dr. Sarah Johnson',
      date: '2023-12-20',
      category: 'Case Studies',
      readTime: '14 min read',
      image: '/images/blog-customer-service.jpg',
      tags: ['Customer Service', 'Chatbots', 'Case Study'],
    },
    {
      id: 6,
      title: 'Ethical AI: Building Trust in Machine Learning Systems',
      excerpt: 'Essential principles and practices for developing ethical and trustworthy AI systems.',
      content: 'As AI systems become more prevalent in our daily lives, ensuring their ethical design...',
      author: 'Michael Chen',
      date: '2023-12-15',
      category: 'AI Strategy',
      readTime: '11 min read',
      image: '/images/blog-ethical-ai.jpg',
      tags: ['Ethical AI', 'Trust', 'Responsible AI'],
    },
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-secondary-50 section-padding">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-6">
              AI Insights & Resources
            </h1>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto mb-8">
              Stay updated with the latest trends, insights, and best practices in artificial intelligence 
              and machine learning from our team of experts.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="bg-white border-b border-secondary-200">
        <div className="container-custom py-8">
          <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary-400" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-primary-600 text-white'
                      : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold text-secondary-900 mb-2">
                No articles found
              </h3>
              <p className="text-secondary-600">
                Try adjusting your search terms or category filter.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white border border-secondary-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="h-48 overflow-hidden">
                    <PlaceholderImage
                      alt={post.title}
                      className="w-full h-full hover:scale-105 transition-transform duration-300"
                      type="blog"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-4 text-sm text-secondary-500 mb-3">
                      <span className="bg-primary-100 text-primary-600 px-2 py-1 rounded-full text-xs font-medium">
                        {post.category}
                      </span>
                      <span className="flex items-center">
                        <CalendarIcon className="w-4 h-4 mr-1" />
                        {formatDate(post.date)}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-secondary-900 mb-3 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-secondary-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-secondary-500">
                        <UserIcon className="w-4 h-4 mr-1" />
                        {post.author}
                      </div>
                      <span className="text-sm text-secondary-500">
                        {post.readTime}
                      </span>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {post.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-secondary-100 text-secondary-600"
                        >
                          <TagIcon className="w-3 h-3 mr-1" />
                          {tag}
                        </span>
                      ))}
                    </div>
                    <button className="w-full mt-4 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors">
                      Read More
                    </button>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="bg-primary-600 section-padding">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Stay Updated
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              Subscribe to our newsletter for the latest AI insights, industry trends, and exclusive content.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg text-secondary-900 focus:ring-2 focus:ring-white focus:outline-none"
              />
              <button className="bg-white text-primary-600 hover:bg-secondary-100 font-medium py-3 px-8 rounded-lg transition-colors duration-200">
                Subscribe
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
